import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IField, IFieldOption } from 'features/assets/model/types/assets'
import { RootState } from 'providers/StoreProvider'
import { assetsApi } from 'shared/api/assetsApi'
import { RecognizedLabel, searchApi, SearchNode, SearchResponse } from 'shared/api/searchApi'

import { SearchTagsType, TagType } from '../types/search'
import { NodeTypes } from '../../../../shared/constants/common'

const isBoard = (node: SearchNode) => node.type === NodeTypes.DIR
const isAsset = (node: SearchNode) => node.type === NodeTypes.FILE
const isUserBoard = (node: SearchNode) => !!node.parent_id


const processSearchResults = (state: SearchState, payload: SearchResponse) => {
  if (payload && payload._embedded) {
    const nodes = payload._embedded?.nodes || []
    const assets = nodes.filter((node: SearchNode) => isAsset(node))
    const boards = nodes.filter((node: SearchNode) => isBoard(node) && isUserBoard(node))

    state.assets = assets
    state.boards = boards

    boardsAdapter.setAll(state.boardsEntities, boards)
    assetsAdapter.setAll(state.assetsEntities, assets)
  } else {
    state.boards = []
    state.assets = []
    boardsAdapter.removeAll(state.boardsEntities)
    assetsAdapter.removeAll(state.assetsEntities)
  }
}

const boardsAdapter = createEntityAdapter<SearchNode>()
const assetsAdapter = createEntityAdapter<SearchNode>()

const boardsSelectors = boardsAdapter.getSelectors()
const assetsSelectors = assetsAdapter.getSelectors()

export const searchBoardsSelectors = {
  ...boardsSelectors,
  selectAll: (state: any) => boardsSelectors.selectAll(state.boardsEntities || boardsAdapter.getInitialState()),
  selectById: (state: any, id: string) => boardsSelectors.selectById(state.boardsEntities || boardsAdapter.getInitialState(), id),
  selectIds: (state: any) => boardsSelectors.selectIds(state.boardsEntities || boardsAdapter.getInitialState())
}

export const searchAssetsSelectors = {
  ...assetsSelectors,
  selectAll: (state: any) => assetsSelectors.selectAll(state.assetsEntities || assetsAdapter.getInitialState()),
  selectById: (state: any, id: string) => assetsSelectors.selectById(state.assetsEntities || assetsAdapter.getInitialState(), id),
  selectIds: (state: any) => assetsSelectors.selectIds(state.assetsEntities || assetsAdapter.getInitialState())
}

interface SearchState {
  inputValue: string
  labels: RecognizedLabel[]
  systemFields: IField[]
  boards: SearchNode[]
  assets: SearchNode[] | null
  boardsEntities: ReturnType<typeof boardsAdapter.getInitialState>
  assetsEntities: ReturnType<typeof assetsAdapter.getInitialState>
  searchTags: IFieldOption[]
  filterTags: IFieldOption[]
  searchAiTags: IFieldOption[] | null
  searchStatus: IFieldOption[] | null
  resultsSearchAiTags: IFieldOption[] | null
  searchTagsType: SearchTagsType
  isLoading: boolean
  isLoadingMenu: boolean
  error: string | null
  searchResults: SearchResponse | null
  isSearchPage: boolean
  isSearchInputActive: boolean
  isTagMenuVisible: boolean
  foundedTags: IFieldOption[]
  foundedAiTags: RecognizedLabel[]
  foundedStatuses: IFieldOption[]
  prevStateSearch: {
    searchAiTags: IFieldOption[]
    searchStatus: IFieldOption[]
  }
  selectedItemsIds: string[]
  lastSelectedIndexes: number[]
  lastSelectedWithShiftIndex: number | null
}

const initialState: SearchState = {
  inputValue: '',
  labels: [],
  systemFields: [],
  boards: [],
  assets: [],
  boardsEntities: boardsAdapter.getInitialState(),
  assetsEntities: assetsAdapter.getInitialState(),
  searchTags: [],
  searchAiTags: [],
  filterTags: [],
  searchStatus: [],
  resultsSearchAiTags: [],
  searchTagsType: SearchTagsType.CONTAIN,
  isLoading: false,
  isLoadingMenu: false,
  error: null,
  searchResults: null,
  isSearchPage: false,
  isSearchInputActive: false,
  isTagMenuVisible: false,
  foundedTags: [],
  foundedAiTags: [],
  foundedStatuses: [],
  prevStateSearch: {
    searchAiTags: [],
    searchStatus: []
  },
  selectedItemsIds: [],
  lastSelectedIndexes: [],
  lastSelectedWithShiftIndex: null,
}

export const fetchLabels = createAsyncThunk(
  'search/fetchLabels',
  async (_, { getState }) => {
    const state = getState() as RootState
    const userId = state.user.user.family_user_id
    const response = await searchApi.getLabels(userId)
    return response
  }
)

export const fetchStatuses = createAsyncThunk(
  'search/fetchStatuses',
  async (_, { getState }) => {
    const state = getState() as RootState
    const userId = state.user.user.family_user_id
    const response = await assetsApi.getSystemFields(userId)
    return response
  }
)

export const fetchSearchResults = createAsyncThunk(
  'search/fetchSearchResults',
  async ({ value }: { value: string }, { getState, dispatch }) => {
    const state = getState() as RootState
    const userId = state.user.user.family_user_id

    const allTags = state.assets.systemFields.find(field => field.title === 'tags')

    const foundedTags = allTags?.options.filter((tag: IFieldOption) => tag.value.toLowerCase().includes(value.toLowerCase()))
    const foundedAiTags = (await searchApi.getLabelsByValue(userId, value) as any)._embedded.labels
    const foundedStatuses = state.assets.systemFields.find(field => field.title === 'status')?.options.filter((status: IFieldOption) => status.value.toLowerCase().includes(value.toLowerCase()))

    dispatch(setFoundedTags(foundedTags))
    dispatch(setFoundedAiTags(foundedAiTags))
    dispatch(setFoundedStatuses(foundedStatuses))

    return await searchApi.search(userId, { labels_or_name_pattern: value + '*' })
  }
)

export const fetchSearchResultsByTagsOrStatuses = createAsyncThunk(
  'search/fetchSearchResultsByTagsOrStatuses',
  async ({
    tagIds,
    searchTagsType = SearchTagsType.CONTAIN,
    searchType = 'tags'
  }: { tagIds: string[], searchTagsType: SearchTagsType, searchType: string }, { getState }) => {
    let response: SearchResponse
    const state = getState() as RootState
    const userId = state.user.user.family_user_id
    const id = state.assets.systemFields.find(field => field.title === searchType)?.id
    switch (searchTagsType) {
    case SearchTagsType.CONTAIN:
      response = await searchApi.search(userId, { field_values: [{ id, filters: { and_values: tagIds } }] })
      break
    case SearchTagsType.CONTAIN_ONE_OF:
      response = await searchApi.search(userId, { field_values: [{ id, filters: { or_values: tagIds } }] })
      break
    case SearchTagsType.DOESNT_CONTAIN:
      response = await searchApi.search(userId, { field_values: [{ id, filters: { not_values: tagIds } }] })
      break
    }
    return response
  }
)

export const fetchSearchResultsByLabel = createAsyncThunk(
  'search/fetchSearchResultsByLabel',
  async ({ label }: { label: string }, { getState }) => {
    const state = getState() as RootState
    const userId = state.user.user.family_user_id
    const response = await searchApi.search(userId, { labels_pattern: label })
    return response
  }
)

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchInputValue: (state, action: PayloadAction<string>) => {
      state.inputValue = action.payload
    },
    setResultsSearchAiTags: (state, action: PayloadAction<IFieldOption[]>) => {
      state.resultsSearchAiTags = action.payload
    },
    setSearchBoards: (state, action: PayloadAction<SearchNode[]>) => {
      state.boards = action.payload
      boardsAdapter.setAll(state.boardsEntities, action.payload)
    },
    setSearchAssets: (state, action: PayloadAction<SearchNode[]>) => {
      state.assets = action.payload
      assetsAdapter.setAll(state.assetsEntities, action.payload)
    },
    setSearchAiTags: (state, action: PayloadAction<IFieldOption[]>) => {
      state.searchAiTags = action.payload
    },
    setSearchStatus: (state, action: PayloadAction<IFieldOption[]>) => {
      state.searchStatus = action.payload
    },
    setIsTagMenuVisible: (state, action: PayloadAction<boolean>) => {
      state.isTagMenuVisible = action.payload
    },
    setIsSearchInputActive: (state, action: PayloadAction<boolean>) => {
      state.isSearchInputActive = action.payload
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setIsLoadingMenu: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMenu = action.payload
    },
    setIsSearchPage: (state, action: PayloadAction<boolean>) => {
      state.isSearchPage = action.payload
    },
    setSearchTagsType: (state, action: PayloadAction<SearchTagsType>) => {
      state.searchTagsType = action.payload
    },
    setSearchTags: (state, action: PayloadAction<IFieldOption[]>) => {
      state.searchTags = action.payload
    },
    setFilterTags: (state, action: PayloadAction<IFieldOption[]>) => {
      state.filterTags = action.payload
    },
    removeFilterTag: (state, action: PayloadAction<string>) => {
      state.filterTags = state.filterTags.filter((tag) => tag.id !== action.payload)
    },
    removeSearchTag: (state, action) => {
      state.searchTags = state.searchTags.filter((tag) => tag.id !== action.payload)
    },
    clearSearchTags: (state) => {
      state.searchTags = []
    },
    setLabels: (state, action) => {
      state.labels = action.payload
    },
    setSearchResultsSearchPage: (state, action: PayloadAction<SearchResponse>) => {
      processSearchResults(state, action.payload)
    },
    setPrevStateSearch: (state) => {
      state.prevStateSearch.searchAiTags = [...state.searchAiTags]
      state.prevStateSearch.searchStatus = [...state.searchStatus]
      state.searchAiTags = []
      state.searchStatus = []
    },
    revertPrevStateSearch: (state) => {
      state.searchAiTags = [...state.prevStateSearch.searchAiTags]
      state.searchStatus = [...state.prevStateSearch.searchStatus]
      state.inputValue = ''
      state.prevStateSearch = {
        searchAiTags: [],
        searchStatus: []
      }
    },
    updateBoardInSearchResults: (state, action: PayloadAction<any>) => {
      state.boards = state.boards.map((board) => board.id === action.payload.id ? { ...board, ...action.payload } : board)
      boardsAdapter.updateOne(state.boardsEntities, {
        id: action.payload.id,
        changes: action.payload
      })
    },
    updateAssetInSearchResults: (state, action: PayloadAction<any>) => {
      state.assets = state.assets.map((asset) => asset.id === action.payload.id ? { ...asset, ...action.payload } : asset)

      assetsAdapter.updateOne(state.assetsEntities, {
        id: action.payload.id,
        changes: action.payload
      })
    },
    removeBoardFromSearchResults: (state, action: PayloadAction<string[]>) => {
      state.boards = state.boards.filter((board) => !action.payload.includes(board.id))
      boardsAdapter.removeMany(state.boardsEntities, action.payload)
    },
    removeAssetFromSearchResults: (state, action: PayloadAction<string[]>) => {
      state.assets = state.assets.filter((asset) => !action.payload.includes(asset.id))
      assetsAdapter.removeMany(state.assetsEntities, action.payload)
    },
    clearPrevStateSearch: (state) => {
      state.prevStateSearch = {
        searchAiTags: [],
        searchStatus: []
      }
    },
    clearSearchResults: (state) => {
      state.searchResults = null
      state.foundedAiTags = []
      state.foundedTags = []
      state.foundedStatuses = []
      state.isLoading = false
    },
    clearSearchAssetsAndBords: (state) => {
      state.assets = []
      state.boards = []
      boardsAdapter.removeAll(state.boardsEntities)
      assetsAdapter.removeAll(state.assetsEntities)
    },
    clearAllTags: (state) => {
      state.searchTags = []
      state.searchAiTags = []
      state.searchStatus = []
    },
    clearSearchState: (state) => {
      state.isSearchPage = false
      state.searchTags = []
      state.searchTagsType = SearchTagsType.CONTAIN
      state.assets = []
      state.boards = []
      state.searchAiTags = []
      state.searchStatus = []
      state.inputValue = ''
      state.foundedTags = []
      state.foundedStatuses = []
      state.foundedAiTags = []
      boardsAdapter.removeAll(state.boardsEntities)
      assetsAdapter.removeAll(state.assetsEntities)
      state.selectedItemsIds = []
      state.lastSelectedIndexes = []
      state.lastSelectedWithShiftIndex = null
    },
    setFoundedTags: (state, action: PayloadAction<IFieldOption[]>) => {
      state.foundedTags = action.payload
    },
    setFoundedAiTags: (state, action: PayloadAction<RecognizedLabel[]>) => {
      state.foundedAiTags = action.payload
    },
    setFoundedStatuses: (state, action: PayloadAction<IFieldOption[]>) => {
      state.foundedStatuses = action.payload
    },
    selectItem: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      const nodes = [...state.boards, ...state.assets]

      const indexOfItemId = state.selectedItemsIds.indexOf(id)
      const indexOfNode = nodes.findIndex(node => node.id === id)

      if (state.selectedItemsIds.length < 2) {
        if (indexOfItemId === -1) {
          state.selectedItemsIds = [id]
        } else {
          state.selectedItemsIds = [...state.selectedItemsIds.slice(0, indexOfItemId), ...state.selectedItemsIds.slice(indexOfItemId + 1)]
        }
      } else {
        state.selectedItemsIds = [id]
      }

      if (state.selectedItemsIds.length) {
        state.lastSelectedIndexes = [indexOfNode]
      } else {
        state.lastSelectedIndexes = []
      }

      state.lastSelectedWithShiftIndex = null
    },
    selectItemWithPressedCtrl: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      const indexOfItemId = state.selectedItemsIds.indexOf(id)

      if (indexOfItemId === -1) {
        state.selectedItemsIds = [...state.selectedItemsIds, id]
      } else {
        state.selectedItemsIds = [...state.selectedItemsIds.slice(0, indexOfItemId), ...state.selectedItemsIds.slice(indexOfItemId + 1)]
      }

      state.lastSelectedWithShiftIndex = null
    },
    selectItemWithPressedShift: (state, action: PayloadAction<{ id: string, items: any }>) => {
      const { id } = action.payload
      const nodes = [...state.boards, ...state.assets]
      const indexOfNode = nodes.findIndex(node => node.id === id)
      const lastSelectedIndex = state.lastSelectedIndexes[state.lastSelectedIndexes.length - 1] || 0
      const lastSelectedWithShiftIndex = state.lastSelectedWithShiftIndex

      let itemsIdsToExclude: string[] = []
      let newIds: string[] = []

      if (lastSelectedWithShiftIndex !== null) {
        if (indexOfNode < lastSelectedWithShiftIndex) {
          itemsIdsToExclude = [
            ...nodes.slice(indexOfNode + 1, lastSelectedIndex),
            ...nodes.slice(lastSelectedIndex + 1, lastSelectedWithShiftIndex + 1)
          ]
            .map(node => node?.id)
            .filter(Boolean)
        } else {
          itemsIdsToExclude = [
            ...nodes.slice(lastSelectedWithShiftIndex, lastSelectedIndex),
            ...nodes.slice(lastSelectedIndex + 1, indexOfNode)
          ]
            .map(node => node?.id)
            .filter(Boolean)
        }
      }

      const newState = state.selectedItemsIds.filter(id => !itemsIdsToExclude.includes(id))

      if (indexOfNode >= lastSelectedIndex) {
        newIds = nodes.slice(lastSelectedIndex, indexOfNode + 1)
          .map(node => node?.id)
          .filter(Boolean)
      } else {
        newIds = nodes.slice(indexOfNode, lastSelectedIndex + 1)
          .map(node => node?.id)
          .filter(Boolean)
      }

      state.selectedItemsIds = Array.from(new Set([...newState, ...newIds]))
      state.lastSelectedWithShiftIndex = indexOfNode
    },
    selectAll: (state) => {
      const allIds = [...state.boards, ...state.assets].map(item => item.id)
      state.selectedItemsIds = allIds
    },
    unselectAll: (state) => {
      state.selectedItemsIds = []
      state.lastSelectedIndexes = []
      state.lastSelectedWithShiftIndex = null
    },
    setSelectedItemsIds: (state, action: PayloadAction<string[]>) => {
      state.selectedItemsIds = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSearchResultsByLabel.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSearchResultsByLabel.fulfilled, (state, action) => {
        state.isLoading = false
        processSearchResults(state, action.payload)
      })
      .addCase(fetchSearchResultsByTagsOrStatuses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSearchResultsByTagsOrStatuses.fulfilled, (state, action) => {
        state.isLoading = false
        processSearchResults(state, action.payload)
      })
      .addCase(fetchSearchResultsByTagsOrStatuses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Произошла ошибка при загрузке результатов поиска'
      })
      .addCase(fetchSearchResults.pending, (state) => {
        state.isLoading = true
        state.isLoadingMenu = true
        state.error = null
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        if(!!state.inputValue.trim()) {
          state.searchResults = action.payload
        }
        processSearchResults(state, action.payload)
        state.isLoadingMenu = false
        state.isLoading = false
      })
      .addCase(fetchStatuses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchStatuses.fulfilled, (state, action) => {
        state.isLoading = false
        state.systemFields = action.payload._embedded.fields
      })
      .addCase(fetchStatuses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Произошла ошибка при загрузке тегов'
      })
      .addCase(fetchLabels.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.isLoading = false
        state.labels = (action.payload as any)._embedded.labels.map((label: RecognizedLabel) => ({
          id: label.id,
          name: label.name,
          type: TagType.AI
        }))
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Произошла ошибка при загрузке AI меток'
      })
  }
})

export const {
  setSearchInputValue,
  setLabels,
  clearSearchResults,
  setSearchTags,
  setSearchTagsType,
  removeSearchTag,
  clearSearchTags,
  clearSearchAssetsAndBords,
  setIsSearchPage,
  clearSearchState,
  setIsLoading,
  setIsSearchInputActive,
  setIsTagMenuVisible,
  setSearchAiTags,
  setSearchStatus,
  clearAllTags,
  setPrevStateSearch,
  revertPrevStateSearch,
  clearPrevStateSearch,
  setSearchBoards,
  setSearchAssets,
  setFoundedTags,
  setFoundedAiTags,
  setFoundedStatuses,
  updateBoardInSearchResults,
  updateAssetInSearchResults,
  removeBoardFromSearchResults,
  removeAssetFromSearchResults,
  selectItem,
  selectItemWithPressedCtrl,
  selectItemWithPressedShift,
  selectAll,
  unselectAll,
  setSearchResultsSearchPage,
  setSelectedItemsIds,
  setFilterTags,
  removeFilterTag
} = searchSlice.actions

const {
  reducer, actions
} = searchSlice

export { actions as searchActions, reducer as searchReducer }
export default searchSlice.reducer
